<template>
  <button
    class="gf-button"
    :class="[`--${variant}`, { '--icon-button': icon }, { '--only-icon-mobile': isOnlyIconMobile }]"
    :type="type"
    :disabled="isDisabled"
    @click="$emit('click')"
  >
    <i
      v-if="beforeIcon"
      class="gf-button__before-icon"
      :class="beforeIcon"
    ></i>
    <i
      v-if="icon"
      class="gf-button__icon"
      :class="icon"
    ></i>
    <i
      v-if="hasMobileIcon"
      class="gf-button__mobile-icon"
      :class="mobileIcon"
    ></i>
    <span
      v-if="text?.length > 0"
      class="gf-button__text"
    >
      {{ text }}
    </span>
    <div
      v-if="$slots.content"
      class="gf-button__content"
    >
      <slot name="content"></slot>
    </div>
    <i
      v-if="afterIcon"
      class="gf-button__after-icon"
      :class="afterIcon"
    ></i>
  </button>
</template>

<script>
export default {
  name: 'GfButton',
  props: {
    type: {
      type: String,
      default: 'button'
    },
    variant: {
      type: String
    },
    text: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    beforeIcon: {
      type: String,
      default: null
    },
    afterIcon: {
      type: String,
      default: null
    },
    showOnlyIconMobile: {
      type: Boolean,
      default: false
    },
    mobileIcon: {
      type: String,
      default: null
    },
    disabled: {
      default: false
    }
  },
  computed: {
    isDisabled() {
      return this.disabled === true || this.disabled === 'true';
    },
    isOnlyIconMobile() {
      return this.showOnlyIconMobile === true || this.showOnlyIconMobile === 'true';
    },
    hasMobileIcon() {
      return this.mobileIcon?.length > 0;
    }
  }
};
</script>

<style lang="scss">
$this: '.gf-button';

#{$this} {
  --gf-button-min-height: 42px;
  --gf-button-border-radius: 6px;
  --gf-button-padding: 8px;
  --gf-button-icon-size: 24px;

  min-height: var(--gf-button-min-height);
  min-width: var(--gf-button-min-height);
  width: fit-content;
  height: fit-content;
  padding: var(--gf-button-padding);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
  border: none;
  border-radius: var(--gf-button-border-radius);
  align-content: center;
  text-align: center;
  vertical-align: middle;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &.--only-icon-mobile #{$this}__text {
    display: none;
  }

  #{$this}__mobile-icon {
    @include theme.md {
      display: none;
    }
  }

  @include theme.md {
    --gf-button-min-height: 34px;
    --gf-button-padding: 5px 10px;
    --gf-button-icon-size: 16px;

    &.--icon-button {
      --gf-button-padding: 9px;
    }
    &.--only-icon-mobile #{$this}__text {
      display: inline;
    }
  }

  & > i {
    font-size: var(--gf-button-icon-size);
  }

  & > * {
    pointer-events: none;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &.--primary {
    color: var(--white);
    background-color: var(--primary);
    transition: background-color 0.1s ease-in-out;

    &:hover {
      color: var(--white);
      background-color: #0069d9;
    }
    &:disabled {
      background-color: var(--primary);
      opacity: 0.65;
    }
  }

  &.--secondary {
    color: var(--text);
    background-color: var(--bg-light);
    transition: background-color 0.1s ease-in-out;

    &:hover {
      background-color: var(--bg-light-selected);
    }
  }

  &.--outline-secondary {
    color: var(--text-muted);
    background-color: transparent;
    border: 1px solid var(--text-muted);
    transition: color 0.1s ease-in-out, border 0.1s ease-in-out;

    &:hover {
      color: var(--text);
      border: 1px solid var(--text);
    }

    &:disabled {
      color: var(--text-muted) !important;
      border: 1px solid var(--text-muted) !important;
      opacity: 0.65;
    }
  }

  &.--muted {
    color: var(--text-muted);
    background-color: var(--light);
    outline: 1px solid var(--border-color);
  }

  &.--white {
    color: var(--text);
    background-color: var(--white);
    outline: 1px solid var(--border-color);
    transition: background-color 0.1s ease-in-out;

    &:hover {
      background-color: var(--light);
    }
  }

  &.--outline-white {
    color: var(--text-muted);
    background-color: var(--white);
    transition: background-color 0.1s ease-in-out;

    &:hover {
      color: var(--text);
    }
  }

  &.--success {
    color: var(--real-white);
    background-color: #28a745;
    transition: background-color 0.1s ease-in-out;

    &:hover {
      background-color: #218838;
    }

    &:disabled {
      background-color: #28a745;
      opacity: 0.65;
    }
  }

  &.--outline-success {
    color: #28a745;
    background-color: transparent;
    border: 1px solid #28a745;
    transition: color 0.1s ease-in-out, border 0.1s ease-in-out;

    &:hover {
      color: #218838;
      background-color: transparent;
      border: 1px solid #218838;
    }

    &:disabled {
      opacity: 0.65;
    }
  }

  &.--warning {
    color: var(--real-white);
    background-color: #ffc107;
    transition: background-color 0.1s ease-in-out;

    &:hover {
      background-color: #e0a800;
    }
  }

  &.--danger {
    color: var(--real-white);
    background-color: #dc3545;
    transition: background-color 0.1s ease-in-out;

    &:hover {
      background-color: #c82333;
    }

    &:disabled {
      background-color: #dc3545;
      opacity: 0.65;
    }
  }

  &.--outline-danger {
    color: #c82333;
    background-color: transparent;
    border: 1px solid #c82333;
    transition: color 0.1s ease-in-out, border 0.1s ease-in-out;

    &:hover {
      color: #dc3545;
      background-color: transparent;
      border: 1px solid #dc3545;
    }

    &:disabled {
      opacity: 0.65;
    }
  }
}
</style>
